import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { fetchAgentDirectoryMetrics, fetchAgents, } from '@/entities/agent';
import { useAgentScope } from '@/features/agent-scope';
import { ApiError } from '@/shared/api';
import { agentScopeEmptyCopy, uiCopy } from '@/shared/copy';
import { Alert, AlertDescription, AlertTitle, Badge, Button, DirectoryPanel, SearchField, Skeleton, Table, TableBody, TableCell, TableHead, TableHeader, TableRow, } from '@/shared/ui';
import { AGENT_LIST_PAGE_SIZE, pageSlice } from '../lib/page-slice';
import { searchAgents } from '../lib/search-agents';
const AGENT_LIST_FETCH_LIMIT = 10_000;
const AVATAR_PALETTE = [
    ['#e8f5e9', '#2e7d32'],
    ['#efecff', '#4b3f9c'],
    ['#fff3e0', '#e65100'],
    ['#e3f2fd', '#1565c0'],
    ['#fce4ec', '#c2185b'],
    ['#e0f7fa', '#00838f'],
];
function formatTemplate(template, values) {
    return Object.entries(values).reduce((text, [key, value]) => text.replace(`{${key}}`, String(value)), template);
}
function pluralAgents(n) {
    const mod10 = n % 10;
    const mod100 = n % 100;
    if (mod10 === 1 && mod100 !== 11)
        return 'Агент';
    if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) {
        return 'Агента';
    }
    return 'Агентов';
}
function formatAgentCode(agentId) {
    if (/^\d+$/.test(agentId))
        return `АГ-${agentId}`;
    return `АГ-${agentId.slice(0, 8)}`;
}
function initials(name) {
    const parts = name.trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0)
        return '?';
    if (parts.length === 1)
        return parts[0].slice(0, 2).toUpperCase();
    return `${parts[0].slice(0, 1)}${parts[1].slice(0, 1)}`.toUpperCase();
}
function avatarColors(agentId) {
    let hash = 0;
    for (let i = 0; i < agentId.length; i += 1) {
        hash = (hash + agentId.charCodeAt(i) * (i + 1)) % 997;
    }
    const pair = AVATAR_PALETTE[hash % AVATAR_PALETTE.length];
    return { bg: pair[0], fg: pair[1] };
}
function hasAgency(agent) {
    const name = agent.agencyName?.trim() ?? '';
    if (!name)
        return false;
    return name.toLocaleLowerCase('ru') !== 'другое ан';
}
function activeEngagementLabel(active) {
    if (active <= 0)
        return uiCopy.agentListNoActiveEngagements;
    if (active === 1)
        return uiCopy.agentListActiveEngagementOne;
    return formatTemplate(uiCopy.agentListActiveEngagementMany, { n: active });
}
function ratingCell(value) {
    if (value == null)
        return uiCopy.agentListEmptyCell;
    return String(value);
}
export function AgentList() {
    const navigate = useNavigate();
    const { scope } = useAgentScope();
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [query, setQuery] = useState('');
    const [page, setPage] = useState(1);
    const [metricsById, setMetricsById] = useState(new Map());
    const [metricsLoading, setMetricsLoading] = useState(false);
    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const data = await fetchAgents(scope, { limit: AGENT_LIST_FETCH_LIMIT });
            setItems(data.items);
            setTotal(data.total);
            setPage(1);
        }
        catch (err) {
            setError(err instanceof ApiError ? err.message : uiCopy.loadFailed);
        }
        finally {
            setLoading(false);
        }
    }, [scope]);
    useEffect(() => {
        void load();
    }, [load]);
    const matched = useMemo(() => searchAgents(items, query), [items, query]);
    useEffect(() => {
        setPage(1);
    }, [query]);
    const paged = useMemo(() => pageSlice(matched, page, AGENT_LIST_PAGE_SIZE), [matched, page]);
    useEffect(() => {
        if (page !== paged.page) {
            setPage(paged.page);
        }
    }, [page, paged.page]);
    const pageIdsKey = paged.items.map((agent) => agent.agentId).join(',');
    useEffect(() => {
        const ids = pageIdsKey ? pageIdsKey.split(',') : [];
        if (ids.length === 0) {
            setMetricsById(new Map());
            return;
        }
        let cancelled = false;
        setMetricsLoading(true);
        void fetchAgentDirectoryMetrics(ids)
            .then((response) => {
            if (cancelled)
                return;
            const next = new Map();
            for (const row of response.items) {
                next.set(row.agentId, row);
            }
            setMetricsById(next);
        })
            .catch(() => {
            if (cancelled)
                return;
            setMetricsById(new Map());
        })
            .finally(() => {
            if (!cancelled)
                setMetricsLoading(false);
        });
        return () => {
            cancelled = true;
        };
    }, [pageIdsKey]);
    const hasQuery = query.trim() !== '';
    const listTruncated = total > items.length;
    const showPager = paged.pageCount > 1;
    const filteredTotal = matched.length;
    if (loading) {
        return _jsx(Skeleton, { className: "h-64 w-full" });
    }
    return (_jsxs("div", { className: "space-y-4", children: [error ? (_jsxs(Alert, { variant: "destructive", children: [_jsx(AlertTitle, { children: uiCopy.errorTitle }), _jsx(AlertDescription, { children: error }), _jsx(Button, { className: "mt-3", variant: "outline", onClick: () => void load(), children: uiCopy.retry })] })) : null, _jsxs("div", { className: "flex flex-wrap items-end justify-between gap-3", children: [_jsx("p", { className: "max-w-3xl text-sm text-muted-foreground", children: uiCopy.agentListIntro }), _jsx("span", { className: "text-[11px] text-muted-foreground", children: uiCopy.agentListIntroHint })] }), items.length === 0 ? (_jsxs(Alert, { children: [_jsx(AlertTitle, { children: agentScopeEmptyCopy(scope).title }), _jsx(AlertDescription, { children: agentScopeEmptyCopy(scope).hint })] })) : (_jsx(DirectoryPanel, { total: _jsxs(_Fragment, { children: [_jsx("strong", { children: filteredTotal.toLocaleString('ru-RU') }), ' ', pluralAgents(filteredTotal)] }), actions: _jsxs(Button, { type: "button", size: "sm", onClick: () => navigate('/agents/new'), children: ["+ ", uiCopy.agentListAddAgent] }), search: _jsx(SearchField, { className: "w-full", value: query, onValueChange: setQuery, placeholder: uiCopy.agentListSearch, "aria-label": uiCopy.agentListSearchLabel }), footer: listTruncated
                    ? formatTemplate(uiCopy.agentListPartialHint, {
                        loaded: items.length,
                        total,
                    })
                    : undefined, children: matched.length === 0 && hasQuery ? (_jsx("div", { className: "px-3 py-4", children: _jsxs(Alert, { children: [_jsx(AlertTitle, { children: uiCopy.engagementTableNoMatches }), _jsx(AlertDescription, { children: "\u0418\u0437\u043C\u0435\u043D\u0438\u0442\u0435 \u0437\u0430\u043F\u0440\u043E\u0441 \u0438\u043B\u0438 \u043E\u0447\u0438\u0441\u0442\u0438\u0442\u0435 \u043F\u043E\u043B\u0435 \u043F\u043E\u0438\u0441\u043A\u0430." })] }) })) : (_jsxs(_Fragment, { children: [_jsxs(Table, { density: "compact", framed: false, "aria-label": "\u0410\u0433\u0435\u043D\u0442\u044B", children: [_jsx(TableHeader, { children: _jsxs(TableRow, { children: [_jsx(TableHead, { children: uiCopy.agentListColAgent }), _jsx(TableHead, { children: uiCopy.agentListColAgency }), _jsx(TableHead, { "data-align": "right", title: uiCopy.agentListRatingAgentTitle, children: uiCopy.agentListColRatingAgent }), _jsx(TableHead, { "data-align": "right", title: uiCopy.agentListRatingEngagementTitle, children: uiCopy.agentListColRatingEngagement }), _jsx(TableHead, { "data-align": "right", title: uiCopy.agentListRatingExternalTitle, children: uiCopy.agentListColRatingExternal }), _jsx(TableHead, { children: uiCopy.agentListColCompleteness }), _jsx(TableHead, { children: uiCopy.agentListColContract }), _jsx(TableHead, { children: uiCopy.agentListColEngagements }), _jsx(TableHead, { "aria-hidden": true })] }) }), _jsx(TableBody, { children: paged.items.map((agent) => {
                                        const metrics = metricsById.get(agent.agentId);
                                        const colors = avatarColors(agent.agentId);
                                        const completeness = agent.completenessPercent ?? 0;
                                        const agencyOk = hasAgency(agent);
                                        const href = `/agents/${encodeURIComponent(agent.agentId)}`;
                                        return (_jsxs(TableRow, { "data-clickable": "true", tabIndex: 0, onClick: () => navigate(href), onKeyDown: (event) => {
                                                if (event.key === 'Enter' || event.key === ' ') {
                                                    event.preventDefault();
                                                    navigate(href);
                                                }
                                            }, children: [_jsx(TableCell, { children: _jsxs("div", { className: "flex items-center gap-2.5", children: [_jsx("span", { className: "grid h-9 w-9 shrink-0 place-items-center rounded-full text-xs font-semibold", style: {
                                                                    backgroundColor: colors.bg,
                                                                    color: colors.fg,
                                                                }, "aria-hidden": true, children: initials(agent.displayName) }), _jsxs("div", { className: "min-w-0", children: [_jsx(Link, { to: href, className: "font-medium text-primary hover:underline", onClick: (event) => event.stopPropagation(), children: agent.displayName?.trim() ||
                                                                            uiCopy.agentListEmptyCell }), _jsxs("div", { className: "text-[11px] text-muted-foreground", children: [agent.phone?.trim() ||
                                                                                uiCopy.agentListEmptyCell, ' · ', formatAgentCode(agent.agentId)] })] })] }) }), _jsx(TableCell, { children: agencyOk
                                                        ? agent.agencyName
                                                        : uiCopy.agentListAgencyUnset }), _jsx(TableCell, { "data-align": "right", children: _jsx("span", { className: (metrics?.ratingAgent ?? 0) > 0
                                                            ? 'font-semibold text-foreground'
                                                            : undefined, children: metricsLoading && !metrics
                                                            ? '…'
                                                            : ratingCell(metrics?.ratingAgent) }) }), _jsx(TableCell, { "data-align": "right", children: metricsLoading && !metrics
                                                        ? '…'
                                                        : ratingCell(metrics?.ratingEngagement) }), _jsx(TableCell, { "data-align": "right", children: metricsLoading && !metrics
                                                        ? '…'
                                                        : ratingCell(metrics?.ratingExternal ?? 0) }), _jsx(TableCell, { children: _jsxs("div", { className: "flex min-w-[7rem] items-center gap-2", children: [_jsx("div", { className: "h-1.5 flex-1 overflow-hidden rounded-full bg-muted", "aria-hidden": true, children: _jsx("div", { className: "h-full rounded-full bg-primary", style: {
                                                                        width: `${Math.min(100, Math.max(0, completeness))}%`,
                                                                    } }) }), _jsxs("span", { className: "tabular-nums text-muted-foreground", children: [completeness, "%"] })] }) }), _jsx(TableCell, { children: agencyOk ? (_jsxs("div", { className: "flex flex-col items-start gap-0.5", children: [metrics?.contractStageTitle ? (_jsx("span", { className: "text-sm", children: metrics.contractStageTitle })) : null, _jsx(Link, { to: "/agencies", className: "text-[11px] text-primary hover:underline", onClick: (event) => event.stopPropagation(), children: uiCopy.agentListOpenAgency })] })) : (_jsx(Button, { type: "button", size: "sm", variant: "outline", onClick: (event) => {
                                                            event.stopPropagation();
                                                            navigate('/agencies');
                                                        }, children: uiCopy.agentListFillAgency })) }), _jsx(TableCell, { children: _jsxs("div", { className: "flex flex-col items-start gap-1", children: [_jsx(Badge, { tone: (metrics?.engagementActive ?? 0) > 0
                                                                    ? 'info'
                                                                    : 'neutral', children: activeEngagementLabel(metrics?.engagementActive ?? 0) }), _jsx("span", { className: "text-[11px] text-muted-foreground", children: formatTemplate(uiCopy.agentListEngagementTotal, {
                                                                    n: metrics?.engagementTotal ?? 0,
                                                                }) })] }) }), _jsx(TableCell, { children: _jsx("span", { className: "text-muted-foreground", "aria-hidden": true, children: "\u203A" }) })] }, agent.agentId));
                                    }) })] }), showPager ? (_jsxs("div", { className: "flex flex-wrap items-center gap-3 px-3 py-3", children: [_jsx(Button, { type: "button", variant: "outline", size: "sm", disabled: paged.page <= 1, onClick: () => setPage((current) => Math.max(1, current - 1)), children: uiCopy.agentListPrevPage }), _jsx("p", { className: "text-sm text-muted-foreground", children: formatTemplate(uiCopy.agentListPageLabel, {
                                        page: paged.page,
                                        pages: paged.pageCount,
                                    }) }), _jsx(Button, { type: "button", variant: "outline", size: "sm", disabled: paged.page >= paged.pageCount, onClick: () => setPage((current) => Math.min(paged.pageCount, current + 1)), children: uiCopy.agentListNextPage }), _jsx("p", { className: "text-sm text-muted-foreground", children: formatTemplate(uiCopy.agentListRangeCount, {
                                        from: paged.from,
                                        to: paged.to,
                                        total: paged.total,
                                    }) })] })) : null] })) }))] }));
}
